import { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Carousel, Skeleton, Row, Col } from 'antd';
import { useTranslation } from 'react-i18next';

import { fetchTopModules } from 'store/slices/moduleSlice';
import Catalog from 'pages/catalog';
import { menuFlatter, getImage } from 'helpers';
import { getHomeBannerSrc } from 'helpers/homeBanners';
import useSmartNavigate from 'hooks/useSmartNavigate';
import useBreakpoint from 'hooks/useBreakpoint';
import useCustomer from 'hooks/useCustomer';
import fetcher, { loadingStatus } from 'helpers/fetcher';
import { PRODUCTS_GRID } from 'constants/breakpoints';
import WishlistButton from 'components/cart/WishlistButton';
import AddToCartButton from 'components/cart/AddToCartButton';
import { formatCurrency, formatWeightWithUnit } from 'helpers/formatter';
import { getStock } from 'helpers/product';
import ProductPriceRow from 'components/product/ProductPriceRow';
import Icon from 'components/Icon';

const Home = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const [popularProducts, setPopularProducts] = useState([]);
	const [popularLoading, setPopularLoading] = useState(true);
	// const bottomModules = useSelector((state) => state.module.bottomModules);
	const topModules = useSelector(state => state.module.topModules);
	const { data: menuData } = useSelector(state => state.menu.categoriesList);
	const { navigate } = useSmartNavigate();
	const { isMobile, isTablet } = useBreakpoint();
	const { customer } = useCustomer();
	const loadRequestIdRef = useRef(0);
	const isMobileBanner = isMobile || isTablet;
	const bannerSrc = getHomeBannerSrc(customer?.store_id ?? 0, isMobileBanner);

	const slideshow = topModules.data.modules?.find(d => d.code === 'slideshow');
	// const featured = bottomModules.data.modules?.find((d) => d.code === "featured");

	useEffect(() => {
		dispatch(fetchTopModules());
		// dispatch(fetchBottomModules());
	}, [dispatch]);

	// Популярные товары — один запрос на бэкенд (product/popular по просмотрам)
	useEffect(() => {
		const currentRequestId = ++loadRequestIdRef.current;
		setPopularLoading(true);

		fetcher('/index.php?route=product/popular&limit=8', { cache: 'no-store' })
			.then(res => res.json())
			.then(data => {
				if (currentRequestId !== loadRequestIdRef.current) return;
				const list = data.products || [];
				const inStock = list.filter(item => {
					const stock = getStock(item);
					return stock.stock > 0;
				});
				setPopularProducts(inStock.slice(0, 4));
			})
			.catch(() => {
				if (currentRequestId !== loadRequestIdRef.current) return;
				setPopularProducts([]);
			})
			.finally(() => {
				if (currentRequestId !== loadRequestIdRef.current) return;
				setPopularLoading(false);
			});

		return () => {
			loadRequestIdRef.current += 1;
		};
	}, []);

	const bannerLink = slideshow?.banners?.[0]?.link;

	return (
		<>
			<div className='region hero-banner-wrapper'>
				<div className='text-center'>
					{loadingStatus.SUCCEEDED === topModules.status ? (
						<Carousel autoplay>
							<div
								data-click-key='banner'
								onClick={() => {
									if (bannerLink) {
										navigate(bannerLink);
									}
								}}
								style={{ cursor: bannerLink ? 'pointer' : 'default' }}
							>
								<img
									key={bannerSrc}
									src={bannerSrc}
									alt={t('common.banner')}
									style={{
										margin: '0 auto',
										width: '100%',
										height: 'auto',
										display: 'block',
									}}
								/>
							</div>
						</Carousel>
					) : (
						<Skeleton.Image active style={{ width: 859, height: 360 }} />
					)}
				</div>
			</div>

			{/* <HeadingTitle title={featured?.heading_title} />
      <List
        grid={PRODUCTS_GRID}
        className="rn-product-grid"
        dataSource={featured?.products || Array(5).fill({})}
        renderItem={(item) => (
          <Skeleton active loading={loadingStatus.SUCCEEDED !== bottomModules.status}>
            <ProductItem item={item} />
          </Skeleton>
        )}
      /> */}

			<Catalog
				breadcrumb={<></>}
				grid={{ ...PRODUCTS_GRID, xs: 2, gutter: 24 }}
			/>

			<div className='region popular-products-section'>
				<div className='popular-products-header'>
					<h2 className='popular-products-title'>
						{t('home.popularProducts')}
					</h2>
					<a
						href='#'
						data-click-key='to-catalog'
						onClick={e => {
							e.preventDefault();
							navigate('/catalog');
						}}
						className='popular-products-link catalog-link-all'
					>
						<span className='catalog-link-text'>{t('home.toCatalog')}</span>
						<Icon name='icon-arrow-right-gray' className='catalog-link-icon' />
					</a>
				</div>
				{!popularLoading && popularProducts.length > 0 ? (
					<Row gutter={24} className='popular-products-list'>
						{popularProducts.map((item, index) => {
							const stock = getStock(item);
							const handleGotoItem = () => {
								if (item.product_id && item.slug) {
									navigate(`/product/${item.product_id}/${item.slug}`);
								}
							};

							return (
								<Col xs={12} sm={12} md={6} key={item.product_id || index}>
									<div
										className='popular-product-card'
										data-click-key={
											item.product_id ? `product:${item.product_id}` : undefined
										}
										onClick={handleGotoItem}
										style={{ cursor: 'pointer' }}
									>
										<div className='popular-product-image-wrapper'>
											<img
												src={item.thumb || getImage(item.image)}
												alt={item.name}
												className='popular-product-image'
											/>
											{item.rating && (
												<div className='popular-product-rating'>
													<Icon
														name='icon-star'
														className='popular-product-rating-icon'
														ariaLabel={t('home.rating')}
														ariaHidden={false}
													/>
													<span className='popular-product-rating-value'>
														{item.rating}
													</span>
												</div>
											)}
										</div>
										<div
											className='popular-product-favorite'
											onClick={e => e.stopPropagation()}
										>
											<WishlistButton
												product_id={item.product_id}
												active={item.in_wishlist}
											/>
										</div>
										<h3 className='popular-product-title'>
											{item.name}, {formatWeightWithUnit(item.weight)}
										</h3>
										<ProductPriceRow item={item} />
										<div onClick={e => e.stopPropagation()}>
											<AddToCartButton item={item} />
										</div>
										<div
											className={`popular-product-stock ${
												stock.stock === 0
													? 'popular-product-stock-unavailable'
													: ''
											}`}
										>
											{stock.stock === 0
												? t('stock.outOfStock')
												: stock.stock === 999
													? t('stock.inStockMany')
													: t('stock.inStockCount', { count: stock.stock })}
										</div>
									</div>
								</Col>
							);
						})}
					</Row>
				) : (
					<Row gutter={24} className='popular-products-list'>
						{Array(4)
							.fill({})
							.map((_, index) => (
								<Col xs={12} sm={12} md={6} key={index}>
									<Skeleton active avatar paragraph={{ rows: 4 }} />
								</Col>
							))}
					</Row>
				)}
			</div>

			<div className='region quality-section'>
				<h2 className='quality-title'>{t('quality.title')}</h2>
				<div className='quality-cards'>
					<div className='quality-card quality-card-gray'>
						<img
							src={`${process.env.PUBLIC_URL || ''}/images/icon-halal.webp`}
							alt=''
							className='quality-card-icon'
						/>
						<h4 className='quality-card-text'>{t('quality.noPork')}</h4>
					</div>
					<div className='quality-card quality-card-gray'>
						<img
							src={`${process.env.PUBLIC_URL || ''}/images/icon-eggs.webp`}
							alt=''
							className='quality-card-icon'
						/>
						<h4 className='quality-card-text'>{t('quality.naturalEggs')}</h4>
					</div>
					<div className='quality-card quality-card-gray'>
						<img
							src={`${process.env.PUBLIC_URL || ''}/images/icon-cow.webp`}
							alt=''
							className='quality-card-icon'
						/>
						<h4 className='quality-card-text'>{t('quality.ownSlaughter')}</h4>
					</div>
					<div className='quality-card quality-card-gray'>
						<img
							src={`${process.env.PUBLIC_URL || ''}/images/icon-nature.webp`}
							alt=''
							className='quality-card-icon'
						/>
						<h4 className='quality-card-text'>
							{t('quality.noPreservatives')}
						</h4>
					</div>
					<div
						className='quality-card quality-card-blue'
						style={{
							backgroundImage: `url(${
								process.env.PUBLIC_URL || ''
							}/images/bg-quality-card.webp)`,
							backgroundSize: 'cover',
							backgroundPosition: 'center',
							backgroundRepeat: 'no-repeat',
						}}
					>
						<Icon name='icon-dna' className='quality-card-icon' />
						<h4 className='quality-card-text quality-card-text-white'>
							{t('quality.noGMO')}
						</h4>
					</div>
				</div>
			</div>

			{/* <div className='region discounts-section'>
				<div className='popular-products-header'>
					<h2 className='popular-products-title'>ВАШИ СКИДКИ</h2>
					<a
						href='/catalog'
						onClick={e => {
							e.preventDefault();
							navigate('/catalog');
						}}
						className='popular-products-link catalog-link-all'
					>
						<span className='catalog-link-text'>В каталог</span>
						<Icon name='icon-arrow-right-gray' className='catalog-link-icon' />
					</a>
				</div>
				<Row gutter={24} className='popular-products-list'>
					{[
						{
							name: 'Ягненок задняя часть (целиком)',
							image: 'catalog/cat-cover/cat-baranina.jpg',
							price: 3956,
							weight: '4,11 кг',
							stock: '6',
							product_id: 1,
							slug: 'yagnenok-zadnyaya-chast',
						},
						{
							name: 'Говядина премиум',
							image: 'catalog/cat-cover/cat-goviadina.jpg',
							price: 2450,
							weight: '2,5 кг',
							stock: '12',
							product_id: 2,
							slug: 'govyadina-premium',
						},
						{
							name: 'Куриные окорочка',
							image: 'catalog/cat-cover/cat-ptica.jpg',
							price: 890,
							weight: '1,2 кг',
							stock: '24',
							product_id: 3,
							slug: 'kurinye-okorochka',
						},
						{
							name: 'Колбаса домашняя',
							image: 'catalog/cat-cover/cat-kolbasy.jpg',
							price: 1250,
							weight: '0,8 кг',
							stock: '8',
							product_id: 4,
							slug: 'kolbasa-domashnyaya',
						},
					].map((product, index) => {
						const stock = getStock(product);
						const discountPercent = Math.floor(Math.random() * 11) + 10; // 10-20%
						const discountedPrice = product.price;
						const originalPrice = Math.round(
							product.price / (1 - discountPercent / 100)
						);
						const formattedDiscountedPrice =
							discountedPrice.toLocaleString('ru-RU');
						const formattedOriginalPrice =
							originalPrice.toLocaleString('ru-RU');

						return (
							<Col xs={12} sm={12} md={6} key={index}>
								<div
									className='popular-product-card'
									onClick={() =>
										navigate(`/product/${product.product_id}/${product.slug}`)
									}
									style={{ cursor: 'pointer' }}
								>
									<div className='popular-product-image-wrapper'>
										<img
											src={product.thumb || getImage(product.image)}
											alt={product.name}
											className='popular-product-image'
										/>
										<div className='discount-badge'>
											<span className='discount-badge-text'>
												-{discountPercent}%
											</span>
										</div>
										<div className='popular-product-rating'>
											<Icon
												name='icon-star'
												className='popular-product-rating-icon'
												ariaLabel='Рейтинг'
												ariaHidden={false}
											/>
											<span className='popular-product-rating-value'>4,6</span>
										</div>
									</div>
									<div
										className='popular-product-favorite'
										onClick={e => e.stopPropagation()}
									>
										<WishlistButton
											product_id={product.product_id}
											active={product.in_wishlist}
										/>
									</div>
									<h3 className='popular-product-title'>
										{product.name}, {product.weight}
									</h3>
									<div className='discount-product-price-row'>
										<div className='discount-price-wrapper'>
											<span className='discount-product-price'>
												{formattedDiscountedPrice} ₽
											</span>
											<span className='discount-product-price-old'>
												{formattedOriginalPrice} ₽
											</span>
										</div>
										<span className='popular-product-weight'>
											{product.weight}
										</span>
									</div>
									<button
										className='popular-product-button'
										onClick={e => {
											e.stopPropagation();
											// TODO: Добавить логику добавления в корзину
										}}
										disabled={
											(stock.quantity === 0 && stock.stock === 0) ||
											stock.stock === 0
										}
									>
										<Icon
											name='icon-shopping-cart'
											className='popular-product-button-icon'
										/>
										<span>В корзину</span>
									</button>
									<div
										className={`popular-product-stock ${
											stock.stock === 0 ? 'popular-product-stock-unavailable' : ''
										}`}
									>
										{stock.stock === 0
											? 'Нет в наличии'
											: `В наличии ${stock.stock === 999 ? 'много' : `${stock.stock} шт.`}`}
									</div>
								</div>
							</Col>
						);
					})}
				</Row>
			</div> */}

			<div className='region about-section'>
				<div className='about-content'>
					<div className='about-text'>
						<h2 className='about-title'>
							<span className='about-title-brand'>{t('home.aboutTitle')}</span>:{' '}
							{t('home.aboutSubtitle')}
						</h2>
						<div className='about-main-text'>
							<p>{t('home.aboutText1')}</p>
							<p>{t('home.aboutText2')}</p>
							<p>{t('home.aboutText3')}</p>
						</div>
						<p className='about-brand-text'>{t('home.aboutBrand')}</p>
					</div>
					<div className='about-image'>
						<img
							src={`${process.env.PUBLIC_URL || ''}/images/about-landscape.webp`}
							alt={t('home.mountainLandscape')}
							className='about-image-img'
						/>
					</div>
				</div>
			</div>
		</>
	);
};

export default Home;
